import React, { useState, useMemo, useEffect } from 'react';
import { UserPlusIcon, UsersIcon, HeartIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { UserPlusIcon as UserPlusIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

interface NewcomerStatusSectionProps {
  totalNewcomers?: number;
  activeNewcomers?: number;
  graduatedNewcomers?: number;
}

// 색상 상수
const NEWCOMER_COLORS = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];
const STATUS_COLORS = ['#10B981', '#3B82F6', '#F59E0B'];
const PROGRESS_COLORS = ['#7C3AED', '#EC4899', '#06B6D4', '#F97316'];

export const NewcomerStatusSection: React.FC<NewcomerStatusSectionProps> = ({
  totalNewcomers = 87,
  activeNewcomers = 72,
  graduatedNewcomers = 15
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'status' | 'progress' | 'trends'>('overview');
  const [animatedValues, setAnimatedValues] = useState({
    totalNewcomers: 0,
    activeNewcomers: 0,
    graduatedNewcomers: 0,
    adaptationRate: 0
  });

  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalNewcomers,
        activeNewcomers,
        graduatedNewcomers,
        adaptationRate: totalNewcomers > 0 ? Math.round((activeNewcomers / totalNewcomers) * 100) : 0
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [totalNewcomers, activeNewcomers, graduatedNewcomers]);

  // 새가족 현황 데이터
  const newcomerData = useMemo(() => [
    {
      name: '활동 중',
      value: activeNewcomers,
      color: '#10B981',
      percentage: totalNewcomers > 0 ? ((activeNewcomers / totalNewcomers) * 100).toFixed(1) : '0',
      description: '현재 활동하는 새가족'
    },
    {
      name: '정착 완료',
      value: graduatedNewcomers,
      color: '#3B82F6',
      percentage: totalNewcomers > 0 ? ((graduatedNewcomers / totalNewcomers) * 100).toFixed(1) : '0',
      description: '교인으로 정착한 가족'
    },
    {
      name: '관찰 중',
      value: Math.max(0, totalNewcomers - activeNewcomers - graduatedNewcomers),
      color: '#F59E0B',
      percentage: totalNewcomers > 0 ? (((totalNewcomers - activeNewcomers - graduatedNewcomers) / totalNewcomers) * 100).toFixed(1) : '0',
      description: '상황을 지켜보는 중'
    }
  ], [totalNewcomers, activeNewcomers, graduatedNewcomers]);

  // 새가족 단계별 데이터
  const progressData = useMemo(() => [
    { stage: '첫 방문', count: 25, rate: 100, color: '#8B5CF6' },
    { stage: '재방문', count: 20, rate: 80, color: '#EC4899' },
    { stage: '소그룹 참여', count: 15, rate: 60, color: '#10B981' },
    { stage: '사역 참여', count: 12, rate: 48, color: '#F59E0B' }
  ], []);

  // 월별 트렌드 데이터 (가상 데이터)
  const trendData = useMemo(() => [
    { month: '1월', 신규: 12, 정착: 3, 활동: activeNewcomers - 15 },
    { month: '2월', 신규: 15, 정착: 4, 활동: activeNewcomers - 10 },
    { month: '3월', 신규: 18, 정착: 6, 활동: activeNewcomers - 5 },
    { month: '4월', 신규: 14, 정착: 5, 활동: activeNewcomers - 2 },
    { month: '5월', 신규: 16, 정착: 7, 활동: activeNewcomers + 2 },
    { month: '6월', 신규: 12, 정착: graduatedNewcomers, 활동: activeNewcomers }
  ], [activeNewcomers, graduatedNewcomers]);

  // 적응률 계산
  const adaptationRate = totalNewcomers > 0 ? Math.round(((activeNewcomers + graduatedNewcomers) / totalNewcomers) * 100) : 0;
  
  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-purple-200 dark:border-purple-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <UserPlusIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">새가족 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">새로운 가족들의 성장과 정착 과정</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            adaptationRate >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            adaptationRate >= 60 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 inline-block ${
              adaptationRate >= 80 ? 'bg-green-500' :
              adaptationRate >= 60 ? 'bg-blue-500' : 'bg-yellow-500'
            } animate-pulse`}></span>
            적응률 {adaptationRate}%
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('status')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'status'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          상태별 현황
        </button>
        <button
          onClick={() => setActiveTab('progress')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'progress'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          정착 과정
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          성장 트렌드
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 핵심 지표 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 새가족</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{animatedValues.totalNewcomers}명</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <UsersIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">활동 중</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{animatedValues.activeNewcomers}명</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <HeartIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">적응률</p>
                <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">{animatedValues.adaptationRate}%</p>
              </div>
              <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <ChartBarIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">새가족 전체 현황</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">상태별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={newcomerData} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {newcomerData.map((entry, index) => (
                          <Cell key={`cell-newcomer-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value}명`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">상세 정보</h4>
                  <div className="space-y-3">
                    {newcomerData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">
                          {item.value}명 ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      <strong>전체 적응률:</strong> {adaptationRate}%
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      활동 중인 새가족과 정착 완료된 가족의 비율
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">상태별 상세 현황</h3>
              <div className="space-y-4">
                {newcomerData.map((item, index) => (
                  <div key={item.name} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">{item.name}</h4>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{item.value}명</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{item.description}</p>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000" 
                        style={{ 
                          backgroundColor: item.color,
                          width: `${item.percentage}%`
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">전체의 {item.percentage}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">새가족 정착 과정</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">단계별 현황</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={progressData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stage" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [`${value}명`, '인원']} />
                      <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]}>
                        {progressData.map((entry, index) => (
                          <Cell key={`cell-progress-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">정착 단계별 분석</h4>
                  <div className="space-y-3">
                    {progressData.map((stage, index) => (
                      <div key={stage.stage} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{stage.stage}</span>
                          <span className="text-sm text-gray-900 dark:text-white font-bold">{stage.count}명</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                            <div 
                              className="h-2 rounded-full transition-all duration-1000" 
                              style={{ 
                                backgroundColor: stage.color,
                                width: `${stage.rate}%`
                              }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{stage.rate}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">새가족 성장 트렌드</h3>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">6개월 추이</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="신규" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="정착" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="활동" stroke="#EC4899" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">성장 추세</p>
                      <p className="text-xl font-bold text-purple-800 dark:text-purple-200">상승세</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-purple-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">정착률</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">{adaptationRate}%</p>
                    </div>
                    <HeartIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-pink-600 dark:text-pink-400 font-medium">전망</p>
                      <p className="text-xl font-bold text-pink-800 dark:text-pink-200">긍정적</p>
                    </div>
                    <UserPlusIcon className="h-8 w-8 text-pink-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};